import { Coins, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { FeeSummary } from '../../types/fees';
import { formatUsd } from '../../types/fees';
import styles from './FeeBreakdownPanel.module.css';

interface Props {
  summary: FeeSummary | null;
  loading?: boolean;
}

export function FeeBreakdownPanel({ summary, loading }: Props) {
  const [expandedFns, setExpandedFns] = useState<Set<string>>(new Set());

  if (loading) {
    return (
      <div className={styles.loading}>
        <Coins size={14} />
        Computing fees...
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={styles.empty}>
        No fee data. Write some code to see the fee breakdown.
      </div>
    );
  }

  const toggleFn = (name: string) => {
    setExpandedFns(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const deployFee = BigInt(summary.deploy_fee_micro);
  const storageFee = BigInt(summary.storage_fee_micro);
  const totalDeployFee = deployFee + storageFee;

  return (
    <div className={styles.feePanel}>
      <div className={styles.feePanelHeader}>
        <Coins size={14} />
        <h4 className={styles.feePanelTitle}>
          Fee Engine: {summary.contract_name}
        </h4>
      </div>

      {/* Deploy cost section — always shown first */}
      {(deployFee > BigInt(0) || storageFee > BigInt(0)) && (
        <>
          <div className={styles.feeTotal}>
            <span className={styles.feeTotalLabel}>
              Deploy cost{' '}
              {summary.deploy_complexity && (
                <span className={`${styles.complexityBadge} ${
                  summary.deploy_complexity === 'Simple' ? styles.complexitySimple :
                  summary.deploy_complexity === 'Medium' ? styles.complexityMedium :
                  styles.complexityComplex
                }`}>
                  {summary.deploy_complexity}
                </span>
              )}
            </span>
            <span className={styles.feeTotalValue}>
              {formatUsd(totalDeployFee.toString())}
            </span>
          </div>

          <div className={styles.feeDeploySection}>
            <div className={styles.feeDeployRow}>
              <span>Base deploy fee</span>
              <span className={styles.feeDeployValue}>
                {summary.deploy_fee_display}
              </span>
            </div>
            {storageFee > BigInt(0) && (
              <div className={styles.feeDeployRow}>
                <span>Storage (per-KB)</span>
                <span className={styles.feeDeployValue}>
                  {formatUsd(summary.storage_fee_micro)}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Per-function breakdown ── */}
      {summary.functions.map(fn => {
        const expanded = expandedFns.has(fn.function_name);
        return (
          <div key={fn.function_name} className={styles.feeFunction}>
            <button
              className={styles.feeFunctionHeader}
              onClick={() => toggleFn(fn.function_name)}
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}
            >
              <span className={styles.feeFunctionName}>
                {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {' '}fn {fn.function_name}()
              </span>
              <span className={styles.feeFunctionFee}>{fn.fee_display}</span>
            </button>

            {expanded && fn.categories && fn.categories.length > 0 && (
              <div>
                {fn.categories.map(cat => (
                  <div key={cat.category} className={styles.feeLineItem}>
                    <span className={styles.feeLineCategory}>
                      {cat.label}
                    </span>
                    <span className={styles.feeLineCount}>
                      ×{cat.count}
                    </span>
                    <span className={styles.feeLineCost}>
                      {cat.total_display}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Deploy fees section */}
    </div>
  );
}
